// Shared Pool state hook — persisted to localStorage so it survives tab
// switches, page reloads, and the round-trip Explorer ↔ Pool Analyzer.
import { useCallback, useEffect, useState } from "react";

const LS_KEY = "timing.pool.v1";
// Hard cap kept generous so users can shortlist many candidates and then
// trim down via `optimize`. Analysis itself only fires at exactly
// POOL_ANALYZE_SIZE (see PoolAnalyzer.jsx).
export const POOL_MAX = 100;
export const POOL_ANALYZE_SIZE = 30;
export const POOL_BUDGET_LIMIT = 200.0;

function readInitial() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
    } catch { /* ignore */ }
    return [];
}

export function useSharedPool() {
    const [pool, setPool] = useState(readInitial);

    useEffect(() => {
        try {
            localStorage.setItem(LS_KEY, JSON.stringify(pool));
        } catch { /* quota errors ignored */ }
        window.dispatchEvent(new Event("fa:pool-sync"));
    }, [pool]);

    useEffect(() => {
        const sync = () => {
            try {
                const raw = localStorage.getItem(LS_KEY);
                const parsed = raw ? JSON.parse(raw) : [];
                if (Array.isArray(parsed)) {
                    setPool((cur) => (JSON.stringify(cur) === JSON.stringify(parsed) ? cur : parsed));
                }
            } catch { /* ignore */ }
        };
        window.addEventListener("fa:pool-sync", sync);
        window.addEventListener("storage", sync);
        return () => {
            window.removeEventListener("fa:pool-sync", sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    const addPlayer = useCallback((entry) => {
        if (!entry || !entry.fpl_id) return false;
        let added = false;
        setPool((cur) => {
            if (cur.find((p) => p.fpl_id === entry.fpl_id)) return cur;
            if (cur.length >= POOL_MAX) return cur;
            added = true;
            return [...cur, entry];
        });
        return added;
    }, []);

    const removePlayer = useCallback((fplId) => {
        setPool((cur) => cur.filter((p) => p.fpl_id !== fplId));
    }, []);

    const clearPool = useCallback(() => setPool([]), []);

    const hasPlayer = useCallback(
        (fplIdOrSlug) => pool.some((p) => p.fpl_id === fplIdOrSlug || p.historical_slug === fplIdOrSlug),
        [pool],
    );

    // Auto-trim: drop the highest-price players (or by given comparator) until
    // both size + budget constraints are met. Returns dropped list.
    const autoTrim = useCallback(({ maxCount = POOL_ANALYZE_SIZE, maxBudget = POOL_BUDGET_LIMIT, comparator } = {}) => {
        const dropped = [];
        setPool((cur) => {
            let next = [...cur];
            const cmp = comparator || ((a, b) => (b.current_price || 0) - (a.current_price || 0));
            while (next.length > maxCount || next.reduce((s, p) => s + (p.current_price || 0), 0) > maxBudget) {
                next.sort(cmp);
                const drop = next.shift();
                if (!drop) break;
                dropped.push(drop);
            }
            return next;
        });
        return dropped;
    }, []);

    const totalPrice = pool.reduce((s, p) => s + (p.current_price || 0), 0);

    return {
        pool,
        setPool,
        addPlayer,
        removePlayer,
        clearPool,
        hasPlayer,
        autoTrim,
        totalPrice,
    };
}
